<script setup lang="ts">
import { computed } from 'vue';
import type { PageDetailRow } from '@/db/content';
import { useUiDisplayLocale } from '@/composables/useUiDisplayLocale';
import { useContentStore } from '@/stores/content';
import SidebarNav from '@/components/navigation/SidebarNav.vue';
import type { ApiImage } from '@/types/images';
import { useAuthStore } from '@/stores/auth';
import CallToActionBlock from '@/components/blocks/CallToActionBlock.vue';
import CheckListBlock from '@/components/blocks/CheckListBlock.vue';
import FormBlock from '@/components/blocks/FormBlock.vue';
import CentralHeadingBlock from '@/components/blocks/CentralHeadingBlock.vue';
import PageLinkCardBlock from '@/components/blocks/PageLinkCardBlock.vue';
import ThreePageLinkBlock from '@/components/blocks/ThreePageLinkBlock.vue';
import ProcessBlock from '@/components/blocks/ProcessBlock.vue';
import PeopleBlock from '@/components/blocks/PeopleBlock.vue';
import PersonBlock from '@/components/blocks/PersonBlock.vue';
import ProcessOverviewBlock from '@/components/blocks/ProcessOverviewBlock.vue';
import ProtectedBlockMessage from './blocks/ProtectedBlockMessage.vue';
import QuoteBlock from '@/components/blocks/QuoteBlock.vue';
import PullQuoteBlock from '@/components/blocks/PullQuoteBlock.vue';
import RichTextBlock from '@/components/blocks/RichTextBlock.vue';
import FeatureCardBlock from '@/components/blocks/FeatureCardBlock.vue';
import StatCardsBlock from '@/components/blocks/StatCardsBlock.vue';
import TickFeatureCardsBlock from '@/components/blocks/TickFeatureCardsBlock.vue';
import PhotoCollageBlock from '@/components/blocks/PhotoCollageBlock.vue';
import NewsArticleBlock from '@/components/blocks/NewsArticleBlock.vue';
import NewsSummaryBlock from '@/components/blocks/NewsSummaryBlock.vue';
import NeedHelpBlock from '@/components/blocks/NeedHelpBlock.vue';
import AuthorisationRequiredMessageBlock from '@/components/blocks/AuthorisationRequiredMessageBlock.vue';
import ImageAndTextCardBlock from '@/components/blocks/ImageAndTextCardBlock.vue';
import {
  authSnapshotFromSession,
  resolveContentBlocksForAuth,
  type ContentBlockItem,
} from '@/components/contentBlocks';

/** Page detail image: optimised (sources + fallback) or legacy (url only). */
type PageImage = ApiImage;

/** Page detail from API/DB: has id and at least title (rest is type-specific). */
type PageDetail = PageDetailRow & { title?: string; image?: PageImage | null; content?: ContentBlockItem[] };

const blockComponents: Record<string, import('vue').Component> = {
  central_heading: CentralHeadingBlock,
  rich_text: RichTextBlock,
  quote: QuoteBlock,
  pull_quote: PullQuoteBlock,
  image_and_text_card: ImageAndTextCardBlock,
  call_to_action: CallToActionBlock,
  check_list: CheckListBlock,
  page_link_card: PageLinkCardBlock,
  three_page_link: ThreePageLinkBlock,
  process_block: ProcessBlock,
  process_overview: ProcessOverviewBlock,
  people: PeopleBlock,
  person: PersonBlock,
  feature_card: FeatureCardBlock,
  stat_cards: StatCardsBlock,
  tick_feature_cards: TickFeatureCardsBlock,
  photo_collage: PhotoCollageBlock,
  news_article: NewsArticleBlock,
  news_summary: NewsSummaryBlock,
  need_help: NeedHelpBlock,
  authorisation_required_message: AuthorisationRequiredMessageBlock,
  form: FormBlock,
  protected_message: ProtectedBlockMessage,
};

type BlockLayoutConfig = {
  /** 1..4 (md breakpoint uses a 4-column grid) */
  tabletSpan?: 1 | 2 | 3 | 4;
  /** 1..6 (lg breakpoint uses a 6-column grid) */
  desktopSpan?: 1 | 2 | 3 | 4 | 5 | 6;
};

const DEFAULT_BLOCK_LAYOUT: Required<BlockLayoutConfig> = {
  // Default to full width unless a block type is explicitly configured.
  tabletSpan: 4,
  desktopSpan: 6,
};

const BLOCK_LAYOUT_REGISTRY: Record<string, BlockLayoutConfig> = {
  // Keep these as explicit 2-up cards on tablet+desktop.
  check_list: {
    tabletSpan: 2,
    desktopSpan: 3,
  },
  feature_card: {
    tabletSpan: 2,
    desktopSpan: 3,
  },
  process_block: {
    tabletSpan: 2,
    desktopSpan: 3,
  },
  /** Three-up on large screens (6-col grid × span 2); two-up on tablet (4-col × span 2). */
  page_link_card: {
    tabletSpan: 2,
    desktopSpan: 2,
  },
};

const props = defineProps<{
  page: PageDetail;
  /** When the page has no promote/hero image, the first image block may be LCP. */
  prioritizeFirstBlockLcp?: boolean;
}>();
const authStore = useAuthStore();
const contentStore = useContentStore();

const currentLocale = useUiDisplayLocale();

const isNormalPageContentStyle = computed(() => {
  const t = (props.page?.meta as { type?: string } | undefined)?.type;
  return t === 'home.NormalPage' && props.page?.header_style === 'content';
});

const contentSidebarModel = computed(() => {
  if (!isNormalPageContentStyle.value) return null;
  const id = props.page?.id;
  if (typeof id !== 'number') return null;
  return contentStore.getNormalPageContentSidebar(currentLocale.value, id);
});

const showContentSidebar = computed(
  () => contentSidebarModel.value != null && contentSidebarModel.value.items.length > 0
);

const contentBlocks = computed(() => {
  const content = props.page?.content;
  if (!Array.isArray(content)) return [];
  return resolveContentBlocksForAuth(
    content as ContentBlockItem[],
    authSnapshotFromSession(authStore.isReady, authStore.isAuthenticated, authStore.currentUser),
    { wagtailContentPreview: contentStore.isCurrentPreviewMode }
  );
});

function blockComponent(type: string) {
  return blockComponents[type] ?? null;
}

function blockLayoutClasses(type: string) {
  const cfg = BLOCK_LAYOUT_REGISTRY[type] ?? {};
  const tablet = cfg.tabletSpan ?? DEFAULT_BLOCK_LAYOUT.tabletSpan;
  const desktop = cfg.desktopSpan ?? DEFAULT_BLOCK_LAYOUT.desktopSpan;
  return [
    `home-page__block--md-${tablet}`,
    `home-page__block--lg-${desktop}`,
  ];
}

/** Props to pass to the block component. Avoids v-binding a string (e.g. rich_text HTML) which Vue would spread as 0, 1, 2... */
function blockProps(block: ContentBlockItem, index: number): Record<string, unknown> {
  const v = block.value;
  if (typeof v === 'string') {
    return { html: v };
  }
  const base = (v ?? {}) as Record<string, unknown>;
  const pageId = props.page?.id;
  if (block.type === 'process_block' || block.type === 'need_help') {
    const out: Record<string, unknown> = {
      ...base,
      ...(block.type === 'process_block'
        ? { wagtailContentPreview: contentStore.isCurrentPreviewMode }
        : {}),
    };
    if (
      typeof pageId === 'number' &&
      typeof block.id === 'string' &&
      block.id.trim()
    ) {
      out.pageId = pageId;
      out.streamBlockId = block.id;
    }
    return out;
  }
  if (props.prioritizeFirstBlockLcp && index === 0) {
    if (block.type === 'photo_collage') {
      return { ...base, lcpHero: true };
    }
    if (block.type === 'image_and_text_card') {
      return { ...base, lcpImage: true };
    }
  }
  return base;
}

const pageDetailJson = computed(() =>
  JSON.stringify(props.page, null, 2)
);
</script>

<template>
    <!-- Page promote image is rendered in PageView (featured home band) when present; not duplicated here -->

    <div
      v-if="showContentSidebar && contentSidebarModel"
      class="home-page__with-sidebar home-page__with-sidebar--section"
    >
      <aside class="home-page__sidebar">
        <SidebarNav
          :title="contentSidebarModel.title"
          :items="contentSidebarModel.items"
        />
      </aside>
      <div
        v-if="contentBlocks.length"
        class="home-page__main page-content page-content-grid"
        role="main"
      >
        <div
          v-for="(block, index) in contentBlocks"
          :key="block.id ?? index"
          class="home-page__block"
          :class="[blockLayoutClasses(block.type), `home-page__block--${block.type}`]"
        >
          <component
            :is="blockComponent(block.type)"
            v-bind="blockProps(block, index)"
          />
        </div>
      </div>
    </div>

    <div
      v-else-if="contentBlocks.length"
      class="page-content page-content-grid"
      :class="{ 'home-page__main-below-content-header': isNormalPageContentStyle }"
      role="main"
    >
      <div
        v-for="(block, index) in contentBlocks"
        :key="block.id ?? index"
        class="home-page__block"
        :class="[blockLayoutClasses(block.type), `home-page__block--${block.type}`]"
      >
        <component
          :is="blockComponent(block.type)"
          v-bind="blockProps(block, index)"
        />
      </div>
    </div>

    <details class="debug-detail" v-if="false">
      <summary>Debug: page detail</summary>
      <pre class="debug-json">{{ pageDetailJson }}</pre>
    </details>
</template>

<style scoped>
article {
  padding: 0;
}

.home-page__with-sidebar {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

/* Sidebar + main: section wrapper gets vertical padding; no-sidebar path uses .home-page__main-below-content-header. */
.home-page__with-sidebar--section {
  margin-top: 0;
  padding-top: 24px;
  padding-bottom: 24px;
}

.home-page__main-below-content-header {
  margin-top: 0;
  padding-top: 0;
  padding-bottom: 0;
}
@media (min-width: 640px) {
  .home-page__main-below-content-header {
    padding-top: 1.5rem;
    padding-bottom: 1.5rem;
  }
}
@media (min-width: 1024px) {
  .home-page__main-below-content-header {
    padding-top: 3rem;
    padding-bottom: 3rem;
  }

  /*
   * Default flex cross-axis is stretch (Tailwind row without items-start). The aside must be
   * as tall as the main column so position:sticky on .sidebar-nav has a tall scroll track; with
   * align-items:flex-start the aside was only ~nav height and scrolled off immediately.
   */
  .home-page__with-sidebar {
    flex-direction: row;
    align-items: stretch;
    gap: 2rem;
  }

  .home-page__sidebar {
    display: flex;
    flex-direction: column;
    flex-shrink: 0;
    width: 16rem;
  }

  .home-page__main {
    flex: 1;
    min-width: 0;
  }
}

.page-content {
  /* Content-style gap: section padding; other pages: PageView header-group margin-bottom. */
  margin-top: 0;
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
}

.page-content-grid {
  display: grid;
  grid-template-columns: 1fr;
  row-gap: 6.25rem;
  column-gap: 1rem;
}

/* Central heading + immediate next block read as one logical section: tighten to 40px. */
.home-page__block--central_heading + .home-page__block {
  margin-top: -3.75rem;
}

.home-page__block {
  min-width: 0;
}

@media (min-width: 768px) {
  .page-content-grid {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }

  .home-page__block--md-1 {
    grid-column: span 1;
  }

  .home-page__block--md-2 {
    grid-column: span 2;
  }

  .home-page__block--md-3 {
    grid-column: span 3;
  }

  .home-page__block--md-4 {
    grid-column: span 4;
  }
}

@media (min-width: 1024px) {
  .page-content-grid {
    grid-template-columns: repeat(6, minmax(0, 1fr));
  }

  .home-page__block--lg-1 {
    grid-column: span 1;
  }

  .home-page__block--lg-2 {
    grid-column: span 2;
  }

  .home-page__block--lg-3 {
    grid-column: span 3;
  }

  .home-page__block--lg-4 {
    grid-column: span 4;
  }

  .home-page__block--lg-5 {
    grid-column: span 5;
  }

  .home-page__block--lg-6 {
    grid-column: span 6;
  }
}

/*
 * Wagtail often uses two StreamField rich_text blocks: one with only <h2>, the next with <p>s.
 * Spacing between h2 and first p is then between grid rows, not inside .block-rich-text > * + *.
 * .page-content-grid gap is 1rem; prototype About uses space-y-6 / sm:space-y-8 (1.5rem / 2rem).
 */
.page-content-grid .home-page__block--rich_text + .home-page__block--rich_text {
  margin-top: 0.5rem;
}

@media (min-width: 640px) {
  .page-content-grid .home-page__block--rich_text + .home-page__block--rich_text {
    margin-top: 1rem;
  }
}

/* Avoid double vertical spacing from known card block margins inside grid cells. */
.page-content-grid :deep(.check-list-block),
.page-content-grid :deep(.process-block) {
  margin-bottom: 0;
}

/* Feature cards can use flex layout when desired */
@media (min-width: 768px) {
  .home-page__block--feature_card {
    display: flex;
  }
}

/* Page link cards share a row (prototype About / "Aprende liutan" three-card strip). */
.home-page__block--page_link_card {
  display: flex;
  flex-direction: column;
}

.page-content-grid .home-page__block--page_link_card :deep(.page-link-card) {
  flex: 1;
  margin-bottom: 0;
}
.page-content + .debug-detail {
  margin-top: 2rem;
}
.placeholder {
  color: var(--color-text-muted, #666);
}
.debug-detail {
  margin-top: 2rem;
  padding: 1rem;
  background: var(--color-debug-bg, #f5f5f5);
  border-radius: 4px;
}
.debug-json {
  margin: 0.5rem 0 0;
  padding: 1rem;
  overflow: auto;
  font-size: 0.75rem;
  white-space: pre-wrap;
  word-break: break-all;
}
</style>
